import { ApiaApi, makeApiaUrl } from '@apia/api';
import { uniqueId } from 'lodash-es';

export type RetrievedImage = {
  base64: string;
  path: string;
};

export type GetImageSize = '256x256' | '512x512' | '1024x1024';

export async function getImageFromGpt({
  prompt,
  ...props
}: {
  prompt: string;
  size?: GetImageSize;
  model: string;
}): Promise<RetrievedImage | null> {
  const res = await ApiaApi.post<{ image: string }>(
    makeApiaUrl({
      action: 'generateImage',
      ...props,
    }),
    {
      postData: {
        prompt,
      },
      postDataTreatement: 'stringify',
    },
  );

  if (res?.data)
    return {
      base64: res.data.image as any as string,
      path: `${uniqueId()}.png`,
    };

  return null;
}

export async function getImageFromDisk(
  inputProps?: Partial<{
    accept: string;
  }>,
): Promise<RetrievedImage | null> {
  return (
    (await getImagesFromDisk({ ...inputProps, multiple: 'false' }))![0] ?? null
  );
}

export function getImagesFromDisk(
  inputProps?: Partial<{
    accept: string;
    multiple: string;
  }>,
): Promise<RetrievedImage[]> {
  return new Promise<RetrievedImage[]>((resolve) => {
    const input = document.createElement('input');
    input.accept = 'image/*';
    input.type = 'file';
    input.style.display = 'none';
    Object.assign(input, inputProps);

    document.body.append(input);

    input.addEventListener('change', (event) => {
      const target = event?.target as HTMLInputElement;
      if (!target?.files) return;

      const retrievedImages: (RetrievedImage | null)[] = [];
      const sources = [...(target.files ?? [])];

      const checkResolve = () => {
        if (sources.length === retrievedImages.length) {
          resolve(retrievedImages.filter((c): c is RetrievedImage => !!c));
        }
      };

      sources.forEach((file) => {
        if (file) {
          const reader = new FileReader();
          reader.onload = function (e) {
            const binary = (e.target?.result as string)?.split(',')[1];
            input.remove();
            retrievedImages.push({
              base64: binary,
              path: file.name,
            });

            checkResolve();
          };
          reader.readAsDataURL(file);
        } else {
          retrievedImages.push(null);
        }

        checkResolve();
      });
    });
    input.click();
  });
}
